/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Stack,
  Backdrop,
  CircularProgress,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import {
  fetchApplicationDetailsCreenShots,
} from "../../Services/redux/Application-details/ApplicationDetailsActions";
import {
  fetchScreenshotDetails,
  fetchDeleteScreenShot,
} from "../../Services/redux/Screenshots-upload/ScreenshotsUploadActions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import ApplicationDetailsErrorDialog from "../ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import PopupDialog from "../PopupDialog";
import { getConfig } from "../../config";
import "./ApplicationDetailsScreenshots.css";
const ALLOWED_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "jpe", "png"]);
const ALLOWED_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/png"]);

const filterValidImageTypes = (
  images,
  setDimensionError,
  setHeaderMessage,
  setComponentName,
  setOpenErrorPopup
) => {
  const validImages = images.filter((image) => {
    const extension = image?.name?.split(".").pop()?.toLowerCase();
    return (
      ALLOWED_IMAGE_EXTENSIONS.has(extension) ||
      ALLOWED_IMAGE_MIME_TYPES.has(image?.type)
    );
  });

  if (validImages.length !== images.length) {
    const errorMsg = "Only JPEG, JPE or PNG-24 files are allowed";
    setDimensionError(errorMsg);
    setHeaderMessage(errorMsg);
    setComponentName("appError");
    setOpenErrorPopup(true);
  }

  return validImages;
};

// Helper function to validate image dimensions
const validateImageDimensions = (image, setDimensionError, setHeaderMessage, setComponentName, setOpenErrorPopup) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        if (img.width !== 1280 || img.height !== 800) {
          const errorMsg = "Image dimensions must be 1280x800";
          reject(new Error(errorMsg));
          setDimensionError(errorMsg);
          setHeaderMessage(errorMsg);
          setComponentName("appError");
          setOpenErrorPopup(true);
        } else {
          resolve(image);
        }
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = e.target.result;
    };

    reader.readAsDataURL(image);
  });
};

// Helper function to process and validate images
const processImages = async (images, setDimensionError, setHeaderMessage, setComponentName, setOpenErrorPopup) => {
  const promises = images.map((image) => 
    validateImageDimensions(image, setDimensionError, setHeaderMessage, setComponentName, setOpenErrorPopup)
  );

  try {
    const results = await Promise.allSettled(promises);
    const validImages = results
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value);
    
    return validImages;
  } catch (error) {
    console.error("Image dimension error:", error);
    throw error;
  }
};

function ApplicationDetailsScreenshots() {
  const dispatch = useDispatch();
  const { appId } = useParams();
  const [dimensionError, setDimensionError] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [componentName, setComponentName] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );

  const currentLanguage = useSelector(
    (state) => state.languageDetails.currentLanguage
  );
  const errors = useSelector((state) => state.errors);

  const uploadScreenShots = useSelector((state) => state.screenshotsDetails);
  const [screenshots, setScreenshots] = useState([]);
  const thirdPartyApps = globalThis.location.href.includes("third-party-apps");
  const fileInputRef = useRef(null);

  const handleCloseDialog = () => {
    setOpenErrorPopup(false);
  };

  const handleSelectedImages = (selectedImages) => {
    const validTypeImages = filterValidImageTypes(
      selectedImages,
      setDimensionError,
      setHeaderMessage,
      setComponentName,
      setOpenErrorPopup
    );

    if (validTypeImages.length === 0) {
      return;
    }

    const promises = validTypeImages.map((image) =>
      validateImageDimensions(
        image,
        setDimensionError,
        setHeaderMessage,
        setComponentName,
        setOpenErrorPopup
      )
    );

    Promise.allSettled(promises)
      .then((results) => {
        const validImages = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value);

        handleUpload(validImages);
      })
      .catch((error) => {
        console.error("Image validation error:", error);
      });
  };

  useEffect(() => {
    // Calculate effective language for OEM admin
    const calculateEffectiveLanguage = () => {
      if (currentLanguage && currentLanguage !== "") {
        return currentLanguage;
      }
      const sessionLang = sessionStorage.getItem("currentLanguage");
      if (sessionLang && sessionLang !== "") {
        return sessionLang;
      }
      const defaultLang = submissionData?.defaultLanguage || submissionData?.defaultLang;
      if (defaultLang) {
        return defaultLang;
      }
      if (submissionData?.descriptions && !Array.isArray(submissionData.descriptions)) {
        const keys = Object.keys(submissionData.descriptions);
        if (keys.length > 0) {
          return keys[0];
        }
      }
      return null;
    };

    const effectiveLang = calculateEffectiveLanguage();

    if (
      getConfig().REACT_APP_PORTAL === "seller" ||
      getConfig().REACT_APP_PORTAL === "domain-approver" ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      const descriptions = submissionData.descriptions.filter((data) => {
        if (data.language == currentLanguage) {
          return data;
        }
      });

      if (
        descriptions &&
        descriptions.length > 0 &&
        descriptions[0].screenshots &&
        descriptions[0].screenshots.length > 0
      ) {

        setScreenshots([...descriptions[0].screenshots]);
      } else if (
        descriptions &&
        descriptions.length > 0 &&
        descriptions[0].screenShots &&
        descriptions[0].screenShots.length > 0
      ) {

        setScreenshots([...descriptions[0].screenShots]);
      } else {
        setScreenshots([]);
      }
    } else if (getConfig().REACT_APP_PORTAL === "oem-admin" && effectiveLang) {
      const descriptions = submissionData.descriptions[effectiveLang];
      if (descriptions?.screenShots && descriptions.screenShots.length > 0) {
        setScreenshots([...descriptions.screenShots]);
      } else {
        setScreenshots([]);
      }
    }
  }, [currentLanguage, submissionData]);

  useEffect(() => {}, [screenshots]);

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const selectedImages = Array.from(event.dataTransfer.files);
    handleSelectedImages(selectedImages);
  };

  const handleImageChange = (event) => {
    const selectedImages = Array.from(event.target.files);
    handleSelectedImages(selectedImages);
  };

  const handleUpload = async (valimdImages) => {
    for (let i = 0; i < valimdImages.length; i++) {
      dispatch(
        fetchScreenshotDetails(
          appId,
          submissionData.id,
          currentLanguage,
          valimdImages[i]
        )
      );
      if (i < valimdImages.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  };

  useEffect(() => {
    if (
      uploadScreenShots !== undefined &&
      !uploadScreenShots.loading &&
      Object.keys(uploadScreenShots.screenshots).length > 0
    ) {
      const newScreenShots = [...screenshots];
      newScreenShots.push(uploadScreenShots.screenshots);
      setScreenshots(newScreenShots);
      dispatch(
        fetchApplicationDetailsCreenShots(submissionData, newScreenShots)
      );
    }
  }, [dispatch, uploadScreenShots]);

  const handleRemoveImage = (imageId, index) => {
    dispatch(
      fetchDeleteScreenShot(appId, submissionData.id, imageId, currentLanguage)
    );
    const newScreenShots = screenshots.filter((_, i) => i !== index);
    setScreenshots(screenshots.filter((_, i) => i !== index));
    dispatch(fetchApplicationDetailsCreenShots(submissionData, newScreenShots));
  };

  const handleClick = () => {
    fileInputRef.current.click(); // Trigger the file input click
  };

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = globalThis.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${filename}.${getImageExtension(url)}`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up the blob URL
      globalThis.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback to opening in new tab
      window.open(url, "_blank");
    }
  };

  const getImageExtension = (url) => {
    const extension = url.split(".").pop().split("?")[0];
    return ["jpg", "jpeg", "png", "gif"].includes(extension.toLowerCase())
      ? extension
      : "jpg";
  };

  return (
    <div id="screenshots" className="screenshots-parent-div">
      <div>
        <p className="text-screenshots">
          JPEG, JPE or PNG-24(without alpha channel). width 1280px . height
          800px. {screenshots.length} of 8
        </p>
      </div>
      {(!uploadScreenShots.loading ||
        (submissionData &&
          Object.keys(submissionData).length > 0 &&
          screenshots &&
          screenshots.length > 0)) && (
        <div
          style={{
            borderBottom:
              submissionData.status === "DRAFT"
                ? "0px !important"
                : "1px solid rgba(18, 20, 21, 0.2) !important",
          }}
        >
          {!uploadScreenShots.loading &&
            screenshots.map((image, index) => {
              return (
                <div key={image.id} className="image-card">
                  <div className="image-preview">
                    {(getConfig().REACT_APP_PORTAL === "seller" ||
                      getConfig().REACT_APP_PORTAL === "global-admin" ||
                      (getConfig().REACT_APP_PORTAL === "domain-approver" &&
                        !thirdPartyApps)) && (
                      <img
                        className="screenshot-application-details-image"
                        src={image.url}
                        alt={`image${index}`}
                        width="100%"
                      />
                    )}
                    {(getConfig().REACT_APP_PORTAL === "oem-admin" ||
                      (getConfig().REACT_APP_PORTAL === "domain-approver" &&
                        thirdPartyApps)) && (
                      <img
                        className="screenshot-application-details-image"
                        src={image.location}
                        alt={`image${index}`}
                        width="100%"
                      />
                    )}
                  </div>
                  <div className="image-lower">
                    <div className="vertical-dots">
                      <MoreVertIcon style={{ color: "#12141599" }} />
                      <MoreVertIcon
                        style={{ color: "#12141599", marginLeft: "-17px" }}
                      />
                    </div>
                    <div className="delete-download">
                      <button
                        onClick={() =>
                          handleDownload(
                            getConfig().REACT_APP_PORTAL === "oem-admin"
                              ? image.location
                              : image.url,
                            `screenshot-${index}`
                          )
                        }
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                        }}
                        aria-label="Download screenshot"
                      >
                        <DownloadIcon style={{ color: "#12141599" }} />
                      </button>

                      {submissionData.status === "DRAFT" && (
                        <button
                          className="delete-button-wrap"
                          onClick={() => handleRemoveImage(image.id, index)}
                        >
                          <DeleteIcon
                            style={{
                              color: "#12141599",
                            }}
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {submissionData &&
        Object.keys(submissionData).length > 0 &&
        submissionData.status === "DRAFT" &&
        screenshots.length < 8 && (
          <div>
            <div>
              <Stack direction="row" alignItems="center" spacing={2}>
                <button
                  className="upload-button"
                  style={{
                    border: "2px dashed  rgba(18, 20, 21, 0.2)",
                    width: "100%",
                    textAlign: "center",
                    cursor: "pointer",
                    height: "60px !important",
                  }}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={handleClick}
                >
                  <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    accept=".jpg,.jpeg,.jpe,.png,image/jpeg,image/png"
                    style={{ display: "none" }}
                  />
                  <p className="text-screenshot-button-text">
                    Drag and drop images here, or click to browse
                  </p>
                </button>
              </Stack>
            </div>
          </div>
        )}
      {errors?.errorMessage != null && errors?.errorType != null && (
        <ApplicationDetailsErrorDialog />
      )}
      {dimensionError && dimensionError !== "" && (
        <PopupDialog
          onClose={handleCloseDialog}
          openErrorPopup={openErrorPopup}
          headerMessage={headerMessage}
          componentName={componentName}
        />
      )}
      {uploadScreenShots.loading && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={uploadScreenShots.loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </div>
  );
}

export default ApplicationDetailsScreenshots;

